/**
 * useNotification — composable do wyświetlania powiadomień w UI.
 * Obsługuje toast-like powiadomienia z auto-dismiss i ręcznym zamykaniem.
 *
 * Użycie:
 *   const { notify, notifications } = useNotification()
 *   notify('Sukces!', 'success')
 */
import { ref, type Ref } from 'vue'

export interface Notification {
  id: number
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  /** Czas życia w ms (domyślnie: 4000) */
  duration: number
}

/* Globalny stan powiadomień (singleton — współdzielony między komponentami) */
const notifications: Ref<Notification[]> = ref([])
let nextId = 1

/**
 * Pokaż powiadomienie toast.
 * @param message Treść powiadomienia
 * @param type Typ: success | error | info | warning (domyślnie: info)
 * @param duration Czas wyświetlania w ms (domyślnie: 4000, 0 = nie znika)
 */
function notify(message: string, type: Notification['type'] = 'info', duration = 4000) {
  const id = nextId++
  const notification: Notification = { id, message, type, duration }
  notifications.value.push(notification)

  /* Auto-dismiss */
  if (duration > 0) {
    setTimeout(() => {
      dismiss(id)
    }, duration)
  }

  return id
}

/** Zamknij powiadomienie po id */
function dismiss(id: number) {
  notifications.value = notifications.value.filter(n => n.id !== id)
}

/** Wyczyść wszystkie powiadomienia */
function clearAll() {
  notifications.value = []
}

export function useNotification() {
  return {
    notifications,
    notify,
    dismiss,
    clearAll,
  }
}
